import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_FATOR,
  CASAS_PRECO,
  CASAS_QUANTIDADE,
  desescalar,
  dividirArredondando,
  escalar,
} from './aritmetica.ts';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import { fatorConversaoSchema } from './conversoes-unidade-medida.ts';

/**
 * Item do fornecedor — o de‑para entre o **código dele** e o insumo da conta
 * (`.spec/015`, decisão 20).
 *
 * É o que faz o segundo XML do mesmo fornecedor custar menos que o primeiro. O
 * tamanho do problema foi medido na importação de 30/08/2026: **17 de 519**
 * descrições de material casaram com o catálogo por texto, já com comparação sem
 * acento, sem caixa e por prefixo. A nomenclatura do fornecedor e a do catálogo
 * não se encontram, e nenhuma das duas está errada.
 *
 * **Cadastro próprio, e não filho no payload do fornecedor.** É a diferença em
 * relação a `insumos_empresa`, que viaja dentro do insumo: ali os filhos se
 * editam junto com o pai, numa tela só. Aqui a linha nasce **na conferência da
 * nota**, uma por vez, enquanto o usuário casa as linhas do XML — e um filho
 * embutido obrigaria aquela tela a regravar o fornecedor inteiro para acrescentar
 * um código, com todo o cadastro dele no payload.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e tetos                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Teto do código, e do que ele espelha: `cProd` da NF-e tem 60.
 *
 * O limite é o do arquivo, e não uma folga escolhida aqui: um código que não cabe
 * é um código que o XML não poderia ter trazido, e recusá-lo na digitação é mais
 * honesto do que truncá-lo e nunca mais casar com o arquivo.
 */
export const TAMANHO_MAXIMO_CODIGO_FORNECEDOR = 60;

/** Teto da descrição, espelhando o `xProd` da NF-e. */
export const TAMANHO_MAXIMO_DESCRICAO_FORNECEDOR = 120;

const codigoFornecedorSchema = z
  .string()
  .trim()
  .min(1, 'Informe o código do produto no fornecedor.')
  .max(
    TAMANHO_MAXIMO_CODIGO_FORNECEDOR,
    `O código tem no máximo ${TAMANHO_MAXIMO_CODIGO_FORNECEDOR} caracteres.`,
  );

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

const camposDoItemSchema = z.object({
  fornecedorId: uuidSchema,
  insumoId: uuidSchema,
  codigoFornecedor: codigoFornecedorSchema,
  /**
   * O `xProd`, como veio.
   *
   * **Não participa de regra nenhuma.** Existe para a conferência reconhecer a
   * linha: o nome do insumo da conta quase nunca é o do fornecedor, e sem esta
   * coluna a tela mostraria "Cimento Portland CP II-E-32 saco 50 kg" ao lado de
   * um código que o usuário não tem como conferir contra o papel.
   */
  descricaoFornecedor: z
    .string()
    .trim()
    .max(
      TAMANHO_MAXIMO_DESCRICAO_FORNECEDOR,
      `A descrição tem no máximo ${TAMANHO_MAXIMO_DESCRICAO_FORNECEDOR} caracteres.`,
    )
    .nullable()
    .prefault(null),
  /**
   * A unidade em que **ele** fatura. Nula quando é a mesma do insumo.
   *
   * Nula é o caso comum, e é o que dispensa o fator: fornecedor e conta contando
   * na mesma unidade não têm o que converter.
   */
  unidadeFornecedorId: uuidSchema.nullable().prefault(null),
  /** Quantas unidades **do insumo** cabem em `fatorDenominador` do fornecedor. */
  fatorNumerador: fatorConversaoSchema.prefault('1'),
  fatorDenominador: fatorConversaoSchema.prefault('1'),
  ativo: z.boolean().prefault(true),
});

/**
 * O payload, com a única regra que cruza dois campos.
 *
 * **Fator diferente de 1/1 exige unidade do fornecedor.** O contrário é legítimo
 * e passa: o fornecedor que fatura em "UN" enquanto a conta chama "PC" tem
 * unidade própria e fator 1, porque as duas siglas significam a mesma coisa. Mas
 * um fator sem unidade diferente não converte nada — ele multiplicaria a
 * quantidade por um número que a tela não tem como explicar, e o erro só
 * apareceria como estoque a mais.
 *
 * A regra vive **aqui, no service e na tela**, nesta ordem (`.spec/002`). Não
 * cabe em `CHECK`: as duas colunas estão na mesma linha, sim, mas o `CHECK` teria
 * de comparar `numerador <> denominador` como texto numérico, e `1` contra
 * `1.000000` são iguais em valor e diferentes em `text` — a comparação certa é
 * sobre `numeric`, e é o que o banco faz na coluna. O `CHECK` existe lá; esta
 * mensagem, no campo, é o que chega ao usuário.
 */
/**
 * A checagem, escrita uma vez.
 *
 * Os dois schemas a aplicam, e uma cópia em cada um divergiria na primeira
 * correção da mensagem — que é o texto que chega ao usuário preso ao campo.
 */
function exigirUnidadeQuandoConverte(
  valor: { unidadeFornecedorId: string | null; fatorNumerador: string; fatorDenominador: string },
  ctx: z.RefinementCtx,
): void {
  if (valor.unidadeFornecedorId !== null) return;
  if (!temConversao(valor)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['unidadeFornecedorId'],
    message:
      'Informe a unidade em que o fornecedor fatura. Um fator de conversão sem unidade diferente multiplicaria a quantidade por um número que a tela não tem como explicar.',
  });
}

export const criarFornecedorItemSchema = camposDoItemSchema.superRefine(
  exigirUnidadeQuandoConverte,
);

export type CriarFornecedorItem = z.infer<typeof criarFornecedorItemSchema>;

/**
 * A edição **não move o fornecedor**.
 *
 * Trocar o fornecedor de um código já gravado não é corrigir: é dizer que aquele
 * código sempre foi de outra empresa, e as notas que o usaram passariam a ser
 * explicadas por um vínculo que não existia quando foram lançadas. O caminho é
 * inativar a linha e cadastrar no fornecedor certo.
 */
export const atualizarFornecedorItemSchema = camposDoItemSchema
  .omit({ fornecedorId: true })
  .superRefine(exigirUnidadeQuandoConverte);

export type AtualizarFornecedorItem = z.infer<typeof atualizarFornecedorItemSchema>;

/**
 * A leitura resolve os quatro nomes por junção.
 *
 * `insumoUnidadeSigla` é a que fecha a leitura da linha: "SC → KG" só se lê com a
 * unidade do insumo ao lado da do fornecedor, e ela está em outra tabela.
 * Resolvê-las no cliente seria uma consulta por linha.
 */
export const fornecedorItemSchema = z.object({
  id: uuidSchema,
  fornecedorId: uuidSchema,
  fornecedorNome: z.string(),
  insumoId: uuidSchema,
  insumoNome: z.string(),
  insumoUnidadeId: uuidSchema,
  insumoUnidadeSigla: z.string(),
  /**
   * A conta contábil **atual** do insumo, desnormalizada da junção.
   *
   * Existe para a entrada de nota por XML: a linha da nota nasce classificada
   * pelo material, com a conta da operação fiscal só de queda
   * (`contaDaLinhaDeNota`). Sem ela, a linha vinda de um XML chegava ao
   * formulário com a conta da operação — ou vazia —, e a classificação do insumo
   * só entrava quando alguém abria o card do item, porque é ali que o efeito que
   * a busca é montado. O sintoma era "falta a conta contábil" no lançamento, e
   * "abri o item e apareceu" na conferência.
   *
   * Da junção, e não copiada: classificar um insumo hoje corrige as notas
   * lançadas amanhã a partir de vínculos antigos. É a mesma escolha de
   * `insumoContaContabilId` no item do pedido.
   *
   * Nula quando o insumo não está classificado.
   */
  insumoContaContabilId: uuidSchema.nullable(),
  codigoFornecedor: z.string(),
  descricaoFornecedor: z.string().nullable(),
  unidadeFornecedorId: uuidSchema.nullable(),
  unidadeFornecedorSigla: z.string().nullable(),
  fatorNumerador: z.string(),
  fatorDenominador: z.string(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type FornecedorItem = z.infer<typeof fornecedorItemSchema>;

export const listaFornecedoresItensSchema = respostaListaSchema(fornecedorItemSchema);
export type ListaFornecedoresItens = z.infer<typeof listaFornecedoresItensSchema>;

/**
 * Listagem.
 *
 * `codigoFornecedor` é **igualdade**, não "contém": é a consulta que a leitura do
 * XML faz, uma vez por linha do arquivo, e ela usa o índice único. "Contém"
 * traria `201.01.028` para quem procurou `201.01.02`, e a tela casaria o material
 * errado sem que ninguém percebesse.
 *
 * `q` é a busca humana, sobre código **e** descrição do fornecedor: quem procura
 * "cimento" digita a descrição, quem tem o papel na mão digita o código.
 */
export const listarFornecedoresItensQuerySchema = listagemDeCadastroSchema.extend({
  fornecedorId: uuidSchema.optional(),
  insumoId: uuidSchema.optional(),
  codigoFornecedor: codigoFornecedorSchema.optional(),
});

export type ListarFornecedoresItensQuery = z.infer<typeof listarFornecedoresItensQuerySchema>;

/**
 * Colunas de ordenação.
 *
 * A queda é o **código**: é a chave de reconhecimento da tabela, e é por ela que
 * quem confere o catálogo do fornecedor percorre a lista.
 */
export const COLUNAS_ORDENAVEIS_FORNECEDOR_ITEM = [
  'codigoFornecedor',
  'descricaoFornecedor',
  'insumoNome',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* A conversão                                                                */
/* -------------------------------------------------------------------------- */

/**
 * O par de fator representa alguma conversão, ou é a identidade?
 *
 * A comparação é sobre os inteiros escalados, e **não** sobre as strings: `1` e
 * `1.000000` são o mesmo fator e dois textos diferentes, e comparar texto faria a
 * mesma linha ser tratada como conversão ou como identidade conforme quantas
 * casas alguém digitou.
 */
export function temConversao(fator: { fatorNumerador: string; fatorDenominador: string }): boolean {
  return (
    escalar(fator.fatorNumerador, CASAS_FATOR) !== escalar(fator.fatorDenominador, CASAS_FATOR)
  );
}

/**
 * A razão do cadastro → **um número só**, com seis casas.
 *
 * É o que a nota grava em `notas_compra_itens.fator_conversao`: ali o fator é
 * registro do que aconteceu, e não a fonte da conta — quem divide é
 * `converterQuantidade`, com a razão exata e um arredondamento só, no fim.
 *
 * Por isso o truncamento desta função **não** contamina a quantidade: 1.000.000 ÷
 * 22.500 vira `44.444444` aqui, e a conversão continua dando 44,4444 peças por
 * metro cúbico, porque ela nunca passa por este número.
 *
 * A identidade devolve `1.000000`, e não nulo: quem decide se a linha teve
 * conversão é `temConversao`, sobre a razão, e não este decimal.
 */
export function fatorEmDecimal(fator: {
  fatorNumerador: string;
  fatorDenominador: string;
}): string {
  const numerador = escalar(fator.fatorNumerador, CASAS_FATOR);
  const denominador = escalar(fator.fatorDenominador, CASAS_FATOR);

  if (denominador === 0n) return desescalar(0n, CASAS_FATOR);

  const ajuste = 10n ** BigInt(CASAS_FATOR);

  return desescalar(dividirArredondando(numerador * ajuste, denominador), CASAS_FATOR);
}

/**
 * A quantidade do documento → a quantidade **na unidade do insumo**.
 *
 * `quantidadeDocumento × numerador ÷ denominador`, com 4 casas na saída. A
 * conta é feita numa multiplicação e uma divisão só, sobre inteiros escalados: as
 * duas escalas de `10^6` do fator se cancelam, e o arredondamento acontece **uma
 * vez**, no fim. É o que a decisão 20 pede, e é o motivo de o fator ser gravado
 * como razão: 1 m³ de peça 3,00 × 0,15 × 0,05 são 1.000.000 ÷ 22.500 peças, e
 * nenhuma escala decimal guarda esse número sem perder a dízima.
 *
 * **A identidade devolve a quantidade intacta**, sem passar por arredondamento —
 * pelo mesmo motivo de `dinheiroProporcional`: multiplicar e dividir pelo mesmo
 * número parece inócuo e não é, e a linha sem conversão nenhuma passaria a
 * divergir do documento na quarta casa.
 */
export function converterQuantidade(
  quantidadeDocumento: string,
  fator: { fatorNumerador: string; fatorDenominador: string },
): string {
  const escalada = escalar(quantidadeDocumento, CASAS_QUANTIDADE);
  if (!temConversao(fator)) return desescalar(escalada, CASAS_QUANTIDADE);

  const numerador = escalar(fator.fatorNumerador, CASAS_FATOR);
  const denominador = escalar(fator.fatorDenominador, CASAS_FATOR);

  return desescalar(dividirArredondando(escalada * numerador, denominador), CASAS_QUANTIDADE);
}

/**
 * O preço por unidade **do insumo**, derivado do valor do documento.
 *
 * `valorInsumo ÷ quantidade`, com 6 casas. Existe porque a decisão 20 fez o
 * dinheiro do documento mandar: `valor_insumo` grava o `vProd` da NF-e, e é o
 * preço unitário que se recalcula para a quantidade convertida — não o contrário.
 *
 * **Na prática o preço derivado reproduz o documento ao centavo**, e é bom saber
 * por quê: com seis casas no preço, o erro máximo é meia unidade da última casa,
 * e o arredondamento meio-para-cima da multiplicação de volta o absorve. 1.000 KG
 * a R$ 5,00 viram 44,4444 PC a R$ 112,500113, e `44,4444 × 112,500113` devolve
 * R$ 5.000,0000222 — R$ 5.000,00 depois de arredondado.
 *
 * O que a decisão 20 garante é o caso em que isso **não** basta: quantidade
 * grande o suficiente para o erro do preço acumular acima de meio centavo. Ali o
 * `valor_insumo` continua sendo o do documento, e a igualdade
 * `quantidade × valor_unitario = valor_insumo` é que cede. As duas alternativas
 * foram descartadas: jogar a diferença em "outros" inventaria despesa acessória
 * que o fornecedor não cobrou, e recusar conversão inexata travaria madeira e
 * chapa, que são os casos que motivaram a feature.
 *
 * **Quantidade zerada devolve zero**, e não uma divisão por zero: não há preço
 * unitário de nada. O `CHECK` da coluna e o schema recusam a linha antes disso; a
 * queda existe para a tela, que calcula enquanto o usuário digita.
 */
export function valorUnitarioDaConversao(valorInsumo: string, quantidade: string): string {
  const escalada = escalar(quantidade, CASAS_QUANTIDADE);
  if (escalada === 0n) return desescalar(0n, CASAS_PRECO);

  const valor = escalar(valorInsumo, CASAS_DINHEIRO);
  const ajuste = 10n ** BigInt(CASAS_PRECO + CASAS_QUANTIDADE - CASAS_DINHEIRO);

  return desescalar(dividirArredondando(valor * ajuste, escalada), CASAS_PRECO);
}

/**
 * O fator que explica a entrega, a partir do saldo que ela atende.
 *
 * Devolvido como **razão**, e na convenção deste cadastro: `fatorNumerador` é
 * quanto do **insumo**, `fatorDenominador` é quanto do **fornecedor**. Ou seja,
 * `saldo ÷ quantidade do documento` — e não o contrário.
 *
 * **A ordem é fácil de inverter, e a inversão é silenciosa**: 1.000 KG que
 * atendem 20 sacos dão `20/1000`, e o par trocado (`1000/20`) converteria os
 * mesmos 1.000 KG em cinquenta mil sacos. Nenhum `CHECK` pega isso — os dois
 * pares são positivos —, e o erro só apareceria como estoque cinquenta vezes
 * maior. É a razão de o teste desta função comparar o **efeito** da conversão, e
 * não as duas strings.
 *
 * **É o saldo em aberto, e não a quantidade pedida**, e a diferença é a segunda
 * armadilha registrada na decisão 20: se uma nota anterior já levou parte,
 * dividir pela quantidade pedida dá fator menor, em silêncio. Na primeira nota
 * contra o pedido os dois números coincidem — é por isso que o erro passa no
 * teste e aparece na terceira entrega. Quem chama passa o saldo; o nome do
 * parâmetro é o lembrete.
 *
 * Devolve `null` quando não há proporção a formar: saldo zerado, ou documento
 * zerado. Quem recebe `null` pergunta o fator em vez de sugerir um.
 */
export function fatorDoAtendimentoTotal(
  quantidadeDoDocumento: string,
  saldoEmAbertoDoItem: string,
): { fatorNumerador: string; fatorDenominador: string } | null {
  const documento = escalar(quantidadeDoDocumento, CASAS_QUANTIDADE);
  const saldo = escalar(saldoEmAbertoDoItem, CASAS_QUANTIDADE);

  if (documento === 0n || saldo === 0n) return null;

  /*
    As duas pontas saem **escaladas em 4 casas**, e não reduzidas.

    A razão `200000/10000000` é a mesma que `20/1000`, e guardar a primeira
    dispensa uma redução por máximo divisor comum que não muda o resultado da
    conversão e esconderia a origem: o par gravado continua legível como "estes
    1.000 do fornecedor atenderam um saldo de 20".
  */
  return {
    fatorNumerador: desescalar(saldo, CASAS_QUANTIDADE),
    fatorDenominador: desescalar(documento, CASAS_QUANTIDADE),
  };
}
